import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ShowdownModals } from "@/components/ShowdownPopups";
import { Zap, LogOut, LayoutDashboard, ListChecks, Trophy, Shield, GitBranch, Swords, X } from "lucide-react";

export const Header = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [playoffsActive, setPlayoffsActive] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/season/current")
      .then((r) => setPlayoffsActive(!!r.data.playoffs_active))
      .catch(() => setPlayoffsActive(false));
  }, [user]);

  const isAdmin = user && (user.role === "admin" || user.role === "owner");
  const nav = user
    ? isAdmin
      ? [
          { to: "/admin", label: "Commissioner", icon: Shield },
          { to: "/showdown", label: "Showdown", icon: Swords },
          { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
        ]
      : [
          { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
          { to: "/picks", label: "Make Picks", icon: ListChecks },
          ...(playoffsActive ? [{ to: "/bracket", label: "Bracket", icon: GitBranch }] : []),
          { to: "/showdown", label: "Showdown", icon: Swords },
          { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
        ]
    : [];

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to={user ? (isAdmin ? "/admin" : "/dashboard") : "/"} data-testid="logo-link" className="flex items-center gap-2 group">
          <div className="h-9 w-9 rounded-lg bg-[#013369] flex items-center justify-center group-hover:shadow-[0_0_18px_rgba(1,51,105,0.6)] transition-shadow">
            <Zap className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-extrabold tracking-tight">
            NFL Pick <span className="neon-text">House</span>
          </span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          {nav.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  active ? "bg-[#1D4ED8]/15 text-[#4C8DFF]" : "text-zinc-400 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{item.label}</span>
              </Link>
            );
          })}
          {user ? (
            <>
              <ChangePasswordDialog />
              <button
                data-testid="logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-zinc-400 hover:text-red-400 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-btn"
              className="px-4 py-2 rounded-full bg-[#D50A0A] text-white text-sm font-bold hover:shadow-[0_0_15px_rgba(213,10,10,0.4)] active:scale-95 transition-transform"
            >
              Sign in
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
};

export const PlayerReminderBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reminder, setReminder] = useState(null);

  const isPlayer = user && user.role === "player";
  useEffect(() => {
    if (!isPlayer) return;
    let alive = true;
    const check = () => api.get("/notifications")
      .then((r) => { if (alive) setReminder(r.data.reminders?.[0] || null); })
      .catch(() => {});
    check();
    const id = setInterval(check, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [isPlayer]);

  if (!isPlayer || !reminder) return null;

  const dismiss = async () => {
    setReminder(null);
    try { await api.post(`/notifications/${reminder.id}/dismiss`); } catch { /* best-effort */ }
  };

  return (
    <div data-testid="player-reminder-banner"
      className="bg-[#D50A0A]/10 border-b border-[#D50A0A]/40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
        <span className="font-display text-sm font-bold text-[#ff7a7a]">🏈 PICKS REMINDER</span>
        <span className="text-sm text-zinc-300">
          You still have picks to finish for <span className="font-semibold text-white">{reminder.week_label || "this week"}</span>.
          You have <span className="font-bold text-white">{reminder.remaining_editable}</span> game{reminder.remaining_editable === 1 ? "" : "s"} remaining. Submit them before kickoff.
        </span>
        <button data-testid="finish-picks-btn" onClick={() => navigate("/picks")}
          className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D50A0A] text-white text-sm font-bold active:scale-95 transition-transform">
          <ListChecks className="h-4 w-4" /> Finish My Picks
        </button>
        <button data-testid="dismiss-reminder-btn" onClick={dismiss} title="Dismiss"
          className="p-2 rounded-lg text-zinc-400 hover:text-white transition-colors"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const PageShell = ({ children, testid }) => (
  <div className="min-h-screen bg-[#050505] grain">
    <Header />
    <PlayerReminderBanner />
    <main data-testid={testid} className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pp-fade-up">
      {children}
    </main>
    <ShowdownModals />
  </div>
);
